import { isAppLogLevelEnabled, type AppLogLevel } from '@/config/runtime-config';
import { createInMemoryIncidentSuppressionStore } from '@/shared/observability/incident-policy';
import { type IncidentDispatchDisposition } from '@/shared/observability/incident-dispatch';

export type ErrorReportingDisposition = IncidentDispatchDisposition;
export type LaunchDarklyIncidentReportingDecision = {
    shouldReport: boolean;
    suppressed: boolean;
    duplicateCount: number;
};
export type LaunchDarklyIncidentReportingReservation = LaunchDarklyIncidentReportingDecision & {
    commit: () => void;
    rollback: () => void;
};

export const ERROR_REPORTING_DISPOSITION_METADATA_KEY = 'errorHandlingDisposition';
export const BOUNDARY_DISPOSITION_METADATA_KEY = 'boundaryDisposition';
export const LAUNCHDARKLY_REPORTABLE_METADATA_KEY = 'launchDarklyReportable';
export const LAUNCHDARKLY_INCIDENT_SUPPRESSION_WINDOW_MS = 60_000;

const isObjectRecord = (value: unknown): value is Record<string, unknown> => {
    return Object.prototype.toString.call(value) === '[object Object]';
};

const readOptionalBoolean = (value: unknown): boolean | null => {
    return typeof value === 'boolean' ? value : null;
};

export const createLaunchDarklyIncidentSuppressionStore = ({
    defaultWindowMs = LAUNCHDARKLY_INCIDENT_SUPPRESSION_WINDOW_MS,
    maxEntries = 256,
}: {
    defaultWindowMs?: number;
    maxEntries?: number;
} = {}) =>
    createInMemoryIncidentSuppressionStore({
        defaultWindowMs,
        maxEntries,
    });

export const evaluateLaunchDarklyIncidentReporting = ({
    fingerprint,
    suppressionStore,
    suppressionWindowMs,
}: {
    fingerprint?: string | null;
    suppressionStore: ReturnType<typeof createLaunchDarklyIncidentSuppressionStore>;
    suppressionWindowMs?: number;
}): LaunchDarklyIncidentReportingDecision => {
    const reservation = reserveLaunchDarklyIncidentReporting({
        fingerprint,
        suppressionStore,
        suppressionWindowMs,
    });

    if (reservation.shouldReport) {
        reservation.commit();
    }

    return {
        shouldReport: reservation.shouldReport,
        suppressed: reservation.suppressed,
        duplicateCount: reservation.duplicateCount,
    };
};

export const reserveLaunchDarklyIncidentReporting = ({
    fingerprint,
    suppressionStore,
    suppressionWindowMs,
}: {
    fingerprint?: string | null;
    suppressionStore: ReturnType<typeof createLaunchDarklyIncidentSuppressionStore>;
    suppressionWindowMs?: number;
}): LaunchDarklyIncidentReportingReservation => {
    const suppressionReservation = suppressionStore.reserve({
        key: fingerprint,
        windowMs: suppressionWindowMs,
    });

    return {
        shouldReport: !suppressionReservation.suppressed,
        suppressed: suppressionReservation.suppressed,
        duplicateCount: suppressionReservation.duplicateCount,
        commit: suppressionReservation.commit,
        rollback: suppressionReservation.rollback,
    };
};

export const annotateLogMetadataWithErrorDisposition = <T extends Record<string, unknown>>(
    metadata: T,
    disposition: ErrorReportingDisposition,
    options?: {
        mirrorToLaunchDarkly?: boolean;
    },
): T & {
    boundaryDisposition: ErrorReportingDisposition;
    errorHandlingDisposition: ErrorReportingDisposition;
    launchDarklyReportable: boolean;
} => ({
    ...metadata,
    [BOUNDARY_DISPOSITION_METADATA_KEY]: disposition,
    [ERROR_REPORTING_DISPOSITION_METADATA_KEY]: disposition,
    [LAUNCHDARKLY_REPORTABLE_METADATA_KEY]: options?.mirrorToLaunchDarkly ?? disposition === 'terminal',
});

export const annotateLogMetadataAsLocalOnly = <T extends Record<string, unknown>>(
    metadata: T,
): T & {
    launchDarklyReportable: false;
} => ({
    ...metadata,
    [LAUNCHDARKLY_REPORTABLE_METADATA_KEY]: false,
});

export const shouldMirrorLogRecordToLaunchDarkly = (
    metadata: unknown,
    options?: {
        level?: unknown;
        minimumLevel?: AppLogLevel;
    },
): boolean => {
    if (!isObjectRecord(metadata)) {
        return isAppLogLevelEnabled({
            level: options?.level,
            minimumLevel: options?.minimumLevel ?? 'trace',
        });
    }

    const explicitFlag = readOptionalBoolean(metadata[LAUNCHDARKLY_REPORTABLE_METADATA_KEY]);
    if (explicitFlag === false) {
        return false;
    }

    return isAppLogLevelEnabled({
        level: options?.level,
        minimumLevel: options?.minimumLevel ?? 'trace',
    });
};

export const stripLaunchDarklyMirrorControlFields = (
    metadata?: Record<string, unknown>,
): Record<string, unknown> | undefined => {
    if (!metadata) {
        return metadata;
    }

    const rest = { ...metadata };
    delete rest[LAUNCHDARKLY_REPORTABLE_METADATA_KEY];
    delete rest[ERROR_REPORTING_DISPOSITION_METADATA_KEY];

    return Object.keys(rest).length > 0 ? rest : undefined;
};
